import { showError } from '@calendly/alert';
import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CopyIcon, DeleteIcon, EditPencilIcon, SettingsMenuVerticalIcon as SettingsIcon } from '@calendly/ui/components/icons';
import { Group, Item } from '@calendly/ui/components/menu';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { Segment } from 'core/analytics/segment';
import { useEventTypeContext } from 'features/event_type_provider';
import { DestroyPopup, useDestroyManagedEventTypeMutation, useDuplicateManagedEventTypeMutation, UserGroupPopup } from 'features/managed_event_types';
import { usePopupSwitcher } from 'hooks/use_popup_switcher';
import { isAdminOrOwnerSelector, isOnlyGroupAdminSelector, userGroupRelationshipsSelector } from 'selectors/current_user';
import { etEditorMenuLayerStyle } from './et_editor_menu_layer';
import { useStateTrigger } from './state';
import { useEventTypeEditorSidebar } from '../../../../hooks';
import { useFormHandleSubmit } from '../../../hooks';
const containerCss = "cmkomhu";
const containerOpenCss = "c1c1z5hw";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    isOpen,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerOpenCss]: isOpen
  }, props.className)} />;
});
const gearButtonCss = "gi388dv";
const GearButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(gearButtonCss, props.className)} />);
const withTracking = (event, funct) => (...args) => {
  funct(...args);
  Segment.track(event);
};
export const MoreDropdownParentMet = () => {
  const dispatch = useDispatch();
  const [toggleDropdown, Wrapper, isExpanded] = useDropdown();
  const {
    openPopup,
    closePopup,
    isPopupOpen
  } = usePopupSwitcher();
  const {
    triggerFunction
  } = useStateTrigger();
  const {
    eventType,
    isCreation
  } = useEventTypeContext();
  const {
    isOpen: isETEditorOpen,
    closeSidebar: closeEventTypeSidebar
  } = useEventTypeEditorSidebar();
  const formHandleSubmit = useFormHandleSubmit({
    validateOnly: isCreation
  });
  const {
    name,
    id,
    owning_user_group: owningUserGroup,
    assigned_organization_memberships_count: membershipsCount,
    assigned_user_groups_count: groupsCount
  } = eventType;
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const isOnlyGroupAdmin = useSelector(isOnlyGroupAdminSelector);
  const userGroupRelationships = useSelector(userGroupRelationshipsSelector);
  const userGroupAdminRelationships = useMemo(() => Object.values(userGroupRelationships).filter(relationship => !!relationship.admin), [userGroupRelationships]);
  const canEdit = isAdminOrOwner || isOnlyGroupAdmin && owningUserGroup;
  const canDuplicate = canEdit || isOnlyGroupAdmin && !owningUserGroup;
  const [duplicateManagedEventType] = useDuplicateManagedEventTypeMutation();
  const [destroyManagedEventType, {
    isLoading: isDestroyProgress
  }] = useDestroyManagedEventTypeMutation();
  const onDestroy = async () => {
    try {
      await destroyManagedEventType({
        id
      });
      closePopup();
      if (isETEditorOpen) {
        closeEventTypeSidebar();
      }
    } catch (e) {
      dispatch(showError(e.error.message));
    }
  };
  const handleApplyDuplicate = async ({
    selectedUserGroup
  }) => {
    const {
      group_uuid: userGroupUuid
    } = selectedUserGroup;
    try {
      await duplicateManagedEventType({
        id,
        userGroupUuid
      }).unwrap();
      closePopup();
    } catch (e) {
      dispatch(showError(e.error.message));
    }
  };
  const duplicateManagedEventTypeHandler = () => {
    if (isAdminOrOwner) {
      if (owningUserGroup) {
        duplicateManagedEventType({
          id,
          userGroupUuid: owningUserGroup.uuid
        });
        return;
      }
      duplicateManagedEventType({
        id
      });
      return;
    }
    if (isOnlyGroupAdmin && userGroupAdminRelationships.length > 1) {
      openPopup('user_group_select')();
      return;
    }
    duplicateManagedEventType({
      id,
      userGroupUuid: userGroupAdminRelationships[0].admin.group_uuid
    });
  };
  const triggerRename = () => {
    if (triggerFunction) triggerFunction('rename');
  };
  const onSettingsClick = async () => {
    formHandleSubmit();
    toggleDropdown();
  };
  return <Container isOpen={isExpanded}>
      <GearButton aria-expanded={isExpanded} title={I18n.t('publisher.event_types.context.toggle_button', {
      name
    })} onClick={onSettingsClick} data-calendly-label="et-settings-cog">
        <SettingsIcon />
      </GearButton>
      <Wrapper>
        <ViewportDropdownMenu fullWidth viewportOffset={{
        top: -32
      }} style={etEditorMenuLayerStyle} mobileMenuStyle={etEditorMenuLayerStyle}>
          <Group>
            <Item onClick={withTracking('event_type.rename.click', triggerRename)}>
              <EditPencilIcon />
              {I18n.t('publisher.event_type_editor.sections.name.settings_menu.rename')}
            </Item>
            {canDuplicate && <Item onClick={withTracking('event_type.clone.click', duplicateManagedEventTypeHandler)}>
                <CopyIcon />
                {I18n.t('publisher.action.duplicate')}
              </Item>}
            {canEdit && <Item onClick={withTracking('event_type.delete.click', openPopup('destroy_managed_event_type'))}>
                <DeleteIcon />
                {I18n.t('publisher.action.delete')}
              </Item>}
          </Group>
        </ViewportDropdownMenu>
      </Wrapper>

      <DestroyPopup eventTypeName={name} membershipsCount={membershipsCount} groupsCount={groupsCount} isOpen={isPopupOpen('destroy_managed_event_type')} onClose={closePopup} isDestroyProgress={isDestroyProgress} onDestroy={onDestroy} />

      <UserGroupPopup isOpen={isPopupOpen('user_group_select')} onClose={closePopup} handleApply={handleApplyDuplicate} owningUserGroup={owningUserGroup} showCancelButton />
    </Container>;
};

require("./more_dropdown_parent_met.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./more_dropdown_parent_met.js");